import { useEffect, useMemo, useState } from "react";
import {
  Building2,
  CalendarDays,
  ListChecks,
  Sparkles,
} from "lucide-react-native";
import { Image, ScrollView, Text, View } from "react-native";
import {
  ActionBanner,
  Dialog,
  DialogActions,
  DialogHeader,
  FieldMessage,
  InlineOptionPicker,
  PrimaryButton,
  DateField,
  MoonFormField,
  SecondaryButton,
  TextField,
} from "@louise/moon-ui";
import { useCanonicalCycleIntake } from "@src/packages/care/clinical-record";
import {
  CYCLE_TYPE_LABEL_KEYS,
  type CycleType,
} from "@src/packages/cycles/hooks/useCycles";
import { useTranslations } from "@src/packages/i18n";
import { EMBER } from "../../../../onboarding/theme";

const CYCLE_TYPES: CycleType[] = [
  "ivf",
  "icsi",
  "iui",
  "fet",
  "oi",
  "egg_freezing",
  "embryo_freezing",
  "sperm_freezing",
];

type PastCycleOutcome =
  "pregnancy" | "no_pregnancy" | "miscarriage" | "cancelled" | "other";

const OUTCOME_OPTIONS: {
  value: PastCycleOutcome;
  labelKey: string;
}[] = [
  {
    value: "pregnancy",
    labelKey: "you.modals.end_cycle.outcome_pregnancy",
  },
  {
    value: "no_pregnancy",
    labelKey: "you.modals.end_cycle.outcome_no_pregnancy",
  },
  {
    value: "miscarriage",
    labelKey: "you.modals.end_cycle.outcome_miscarriage",
  },
  {
    value: "cancelled",
    labelKey: "you.modals.end_cycle.outcome_cancelled",
  },
  {
    value: "other",
    labelKey: "you.modals.end_cycle.outcome_other",
  },
];

export default function AddPastCycleDialog({
  visible,
  onClose,
}: {
  visible: boolean;
  onClose: () => void;
}) {
  const t = useTranslations();
  const { save: saveCanonicalCycle, isPending: isSavingCanonicalCycle } =
    useCanonicalCycleIntake();
  const [cycleType, setCycleType] = useState<CycleType | null>(null);
  const [clinic, setClinic] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [outcome, setOutcome] = useState<PastCycleOutcome | null>(null);
  const [notes, setNotes] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const isPreservationCycle =
    cycleType === "egg_freezing" ||
    cycleType === "embryo_freezing" ||
    cycleType === "sperm_freezing";

  const cycleTypeOptions = useMemo(
    () =>
      CYCLE_TYPES.map((value) => ({
        value,
        label: t(CYCLE_TYPE_LABEL_KEYS[value] ?? value),
      })),
    [t],
  );

  useEffect(() => {
    if (!visible) return;
    setCycleType(null);
    setClinic("");
    setStartDate("");
    setEndDate("");
    setOutcome(null);
    setNotes("");
    setFormError(null);
    setSaveError(null);
  }, [visible]);

  const handleClose = () => {
    if (isSavingCanonicalCycle) return;
    onClose();
  };

  const handleSave = async () => {
    setFormError(null);
    setSaveError(null);
    if (!cycleType || !startDate) {
      setFormError(t("care.all_past_cycles.validation_required"));
      return;
    }
    if (endDate && endDate < startDate) {
      setFormError(t("care.all_past_cycles.validation_dates"));
      return;
    }

    const clinicName = clinic.trim() || null;

    try {
      await saveCanonicalCycle({
        cycleType,
        status: "closed",
        description: notes.trim() || null,
        startDate,
        endDate: endDate || null,
        provider: clinicName,
        clinic: clinicName,
        ...(outcome && !isPreservationCycle
          ? {
              outcome: {
                type: outcome,
                dateRecorded: endDate || startDate,
                notes: notes.trim() || null,
              },
            }
          : {}),
      });
      onClose();
    } catch {
      setSaveError(t("care.all_past_cycles.save_error"));
    }
  };

  return (
    <Dialog
      size="wide"
      visible={visible}
      onClose={handleClose}
      layout="centered"
      closeButton="minimal"
      dismissOnBackdropTap={!isSavingCanonicalCycle}
    >
      <Image
        source={require("../../../../assets/images/past-cycle-dialog-illustration.png")}
        style={{
          alignSelf: "center",
          width: 78,
          height: 78,
        }}
        resizeMode="contain"
      />
      <DialogHeader
        align="center"
        title={t("care.all_past_cycles.add_title")}
        subtitle={t("care.all_past_cycles.add_subtitle")}
        style={{ marginTop: 12, marginBottom: 18 }}
      />

      <ScrollView
        style={{ maxHeight: 440 }}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <MoonFormField
          label={t("care.all_past_cycles.type_label")}
          labelIcon={ListChecks}
        >
          <InlineOptionPicker
            options={cycleTypeOptions.map((option) => ({
              id: option.value,
              label: option.label,
              action: "cycle_type_option",
            }))}
            value={cycleType ?? ""}
            onChange={(next) => {
              const match = cycleTypeOptions.find(
                (option) => option.value === next,
              );
              if (match) setCycleType(match.value);
            }}
            wrap
            columns={2}
          />
        </MoonFormField>

        <View style={{ marginTop: 16 }}>
          <TextField
            label={t("care.all_past_cycles.clinic_label")}
            labelIcon={Building2}
            value={clinic}
            onChangeText={setClinic}
            placeholder={t("care.all_past_cycles.clinic_placeholder")}
          />
        </View>

        <View style={{ flexDirection: "row", gap: 10, marginTop: 16 }}>
          <View style={{ flex: 1 }}>
            <DateField
              label={t("care.all_past_cycles.start_date_label")}
              labelIcon={CalendarDays}
              value={startDate}
              onChange={setStartDate}
              required
            />
          </View>
          <View style={{ flex: 1 }}>
            <DateField
              label={t("care.all_past_cycles.end_date_label")}
              labelIcon={CalendarDays}
              value={endDate}
              onChange={setEndDate}
            />
          </View>
        </View>

        <View style={{ marginTop: 16 }}>
          {isPreservationCycle ? (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                lineHeight: 17,
                color: EMBER.colors.textLight,
              }}
            >
              {t("care.all_past_cycles.preservation_outcome_unavailable")}
            </Text>
          ) : (
            <MoonFormField
              label={t("care.all_past_cycles.outcome_label")}
              labelIcon={Sparkles}
            >
              <InlineOptionPicker
                options={OUTCOME_OPTIONS.map((option) => ({
                  id: option.value,
                  label: t(option.labelKey),
                  action: "cycle_outcome_option",
                }))}
                value={outcome ?? ""}
                // Tapping the selected outcome again clears it.
                onChange={(next) => {
                  const match = OUTCOME_OPTIONS.find(
                    (option) => option.value === next,
                  );
                  if (!match) return;
                  setOutcome(outcome === match.value ? null : match.value);
                }}
                wrap
                columns={2}
              />
            </MoonFormField>
          )}
        </View>

        <View style={{ marginTop: 16 }}>
          <TextField
            label={t("care.all_past_cycles.notes_label")}
            value={notes}
            onChangeText={setNotes}
            placeholder={t("care.all_past_cycles.notes_placeholder")}
            numberOfLines={3}
            notes
          />
        </View>

        {formError ? (
          <FieldMessage style={{ marginTop: 12 }}>{formError}</FieldMessage>
        ) : null}
        {saveError ? (
          <ActionBanner
            variant="important"
            message={saveError}
            style={{ marginTop: 12 }}
          />
        ) : null}
      </ScrollView>

      <DialogActions style={{ marginTop: 18 }}>
        <SecondaryButton
          label={t("care.all_past_cycles.cancel_button")}
          onPress={handleClose}
          disabled={isSavingCanonicalCycle}
          flex={1}
        />
        <PrimaryButton
          label={t("care.all_past_cycles.save_button")}
          onPress={handleSave}
          loading={isSavingCanonicalCycle}
          flex={1}
        />
      </DialogActions>
    </Dialog>
  );
}
